import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

// Bee Icon Component
const BeeIcon = ({ className = "w-8 h-8" }) => (
  <img 
    src="/images/logos/honeycomb-logo.png" 
    alt="Queen B" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Clean navigation with services and content
  const navLinks = [
    { name: 'Build', path: '/build' },
    { name: 'Protect', path: '/protect' },
    { name: 'Grow', path: '/grow' },
    { name: 'Academy', path: '/academy' },
    { name: 'Resources', path: '/toolkit' },
    { name: 'Chronicles', path: '/carrier-chronicles' },
    { name: 'Solutions', path: '/business-services' },
  ];

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/95 backdrop-blur-md' : 'bg-black/80'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo & Brand */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <BeeIcon className="w-8 h-8" />
              <div className="flex flex-col">
                <span style={{fontFamily: 'Barlow Condensed, sans-serif'}} className="font-bold text-xl md:text-2xl text-white tracking-tight leading-none">
                  Queen B Logistics
                </span>
                <span className="text-[#D4AF37] text-xs font-medium tracking-wider uppercase hidden sm:block">
                  Premium Freight Operations
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-sm uppercase tracking-widest font-medium transition-colors ${
                    location.pathname === link.path || location.pathname.startsWith(link.path + '/')
                      ? 'text-[#D4AF37]' 
                      : 'text-white/80 hover:text-[#D4AF37]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/intake"
                className="bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
                data-testid="join-hive-btn"
              >
                Join the Hive
              </Link>
              <Link
                to="/hive/access"
                className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="access-hive-btn"
              >
                Access The Hive
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20" />

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="p-6">
          <div className="flex justify-between items-center mb-8">
            <div>
              <span style={{fontFamily: 'Barlow Condensed, sans-serif'}} className="font-bold text-xl text-white block">Queen B Logistics</span>
              <span className="text-[#D4AF37] text-xs">Premium Freight Operations</span>
            </div>
            <button onClick={() => setMobileMenuOpen(false)}>
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          
          <nav className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`block py-4 text-lg font-bold uppercase tracking-wider border-b border-white/10 ${
                  location.pathname === link.path 
                    ? 'text-[#D4AF37]' 
                    : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="mt-8 pt-8 border-t border-white/10 space-y-4">
            <Link
              to="/intake"
              className="block w-full bg-[#D4AF37] text-black py-4 text-center font-bold uppercase tracking-wider"
            >
              Join the Hive
            </Link>
            <Link
              to="/hive/access"
              className="block w-full border border-[#D4AF37] text-[#D4AF37] py-4 text-center font-bold uppercase tracking-wider"
            >
              Access The Hive
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
